import React, { useState, useEffect } from 'react'
import { Box, Flex, Group, Button, Image, Text, FocusTrap, Select, Input, Textarea } from '@mantine/core'
import { Dropzone } from '@mantine/dropzone'
import { IconTrash } from '@tabler/icons-react'
import { notificationSuccess, notificationError } from '../../../../ui/Notifications'
import { IconPhotoPlus } from '@tabler/icons-react'
import { getAllStatusesList } from '../../../../../services/status'
import LoadingData from '../../../../ui/LoadingData'
import { validation } from '../../../../../plugins/validation'
import { addOrderQc } from '../../../../../services/quality-control'

const defaultVal = {
  image: null,
  status: '',
  note: ''
}

const formValidation = {
  image: {
    isError: false,
    message: ''
  },
  status: {
    isError: false,
    message: ''
  },
}

const FormAddOrderQc = ({ idOrder, idOrderItem, onCloseForm, reloadList }) => {
  const [formData, setFormData] = useState(defaultVal)
  const [validationForm, setValidationForm] = useState(formValidation)
  const [loadingForm, setLoadingForm] = useState(false)
  const [loadingStatus, setLoadingStatus] = useState(true)
  const [statusList, setStatusList] = useState([])
  const [previewImage, setPreviewImage] = useState(null)

  const submitOrderQc = async (form) => {
    setLoadingForm(true)
    setValidationForm(formValidation)
    const validateForm = {
      image: form.image === null ? '' : form.image,
      status: form.status
    }
    const isError = validation(validateForm, setValidationForm)
    if (isError) {
      setLoadingForm(false)
      return
    }

    const payload = new FormData()
    payload.append('image', form.image)
    payload.append('note', form.note)
    payload.append('status', Number(form.status))
    payload.append(idOrder !== null ? 'order_id' : 'order_item_id', idOrder !== null ? idOrder : idOrderItem)
    try {
      const response = await addOrderQc(payload)
      if (response.success) {
        notificationSuccess('Add Order QC Successfully', 'You have been added new QC')
        onCloseForm()
        reloadList()
      }
    } catch (error) {
      console.log(error)
      const errorMessage = error.response.data.message
      notificationError('Add Order QC Error', `${errorMessage ? errorMessage : 'Please recheck your form'}`)
    } finally {
      setLoadingForm(false)
    }
  }

  const handleGetStatusList = async () => {
    setLoadingStatus(true)
    const params = {
      'filter[status_type][is]': 9
    }
    try {
      const response = await getAllStatusesList(params)
      if (response.success) {
        const resData = response.data
        const remapData = resData.map((val) => {
          return {
            value: `${val.attributes.id}`,
            label: val.attributes.name.charAt(0).toUpperCase() + val.attributes.name.slice(1)
          }
        })
        setStatusList(remapData)
      } else {
        setStatusList([])
      }
    } catch (error) {
      console.log(error)
    } finally {
      setLoadingStatus(false)
    }
  }

  useEffect(() => {
    handleGetStatusList()
  }, [])

  const handleChangeForm = (name, val) => {
    setFormData((oldVal) => ({ ...oldVal, [name]: val}))
  }

  const handleUploadImage = (file) => {
    const fileEncoded = file[0]
    const fileReader = new FileReader()
    fileReader.readAsDataURL(fileEncoded)
    fileReader.onload = (reader) => {
      setPreviewImage(reader.target.result)
      handleChangeForm('image', fileEncoded)
    }
  }

  const handleErrorFile = (file) => {
    setValidationForm(formValidation)
    const fileState = Object.values(file)
    const errorMessage = fileState[0].errors[0].message
    if (errorMessage) {
      const error = {
        image: {
          isError: true,
          message: errorMessage === 'File is larger than 2000000 bytes'
            ? 'The maximum allowed image size is 2mb'
            : errorMessage
        }
      }
      setValidationForm((oldVal) => ({ ...oldVal, ...error}))
      notificationError('Upload Image Error', validationForm.image.message)
    }
  }

  const handleRemoveImage = () => {
    handleChangeForm('image', null)
    setPreviewImage(null)
  }

  const showPreviewImage = (dataImage) => {
    if (dataImage === null) {
      return (
        <Dropzone
            h={250}
            accept={[
              'image/png',
              'image/jpeg',
              'image/jpg'
            ]}
            onDrop={(files) => handleUploadImage(files)}
            onReject={(files) => handleErrorFile(files)}
            maxSize={2000000}
            style={{ backgroundColor: 'transparent' }}
          >
          <Box ta='center' my={60}>
            <Flex justify='center' mb={10}>
              <Flex justify='center' mb={10}>
                <IconPhotoPlus size={50} color='gray' />
              </Flex>
            </Flex>
            <Box>
              <Text fz={12} c='blue.9'>Upload Image QC Here</Text>
              <Text fz={12} c='gray.6'>PNG, JPG, GIF up to 2 MB</Text>
            </Box>
          </Box>
        </Dropzone>
      )
    } else {
      return (
        <Box>
          <Box style={{ border: '1px dashed', borderColor: '#ced4da', borderRadius: '10px' }}>
            <Image h={250} fit='contain' src={previewImage} radius={10} />
          </Box>
          <Flex justify='center' mt={16}>
            <Button  size='xs' leftSection={<IconTrash size={14} />} variant="filled" onClick={() => handleRemoveImage()}>Remove Image</Button>
          </Flex>
        </Box>
      )
    }
  }

  const loadForm = () => {
    return (
      <Box>
        <FocusTrap.InitialFocus />
        <Box mb='sm'>
          <Input.Wrapper
            label='Upload Image QC'
            size='xs'
            error={validationForm.image.isError ? `${validationForm.image.message}` : ''}
            withAsterisk
          >
            {showPreviewImage(formData.image)}
          </Input.Wrapper>
        </Box>
        <Box mb='sm'>
          <Select
            name='status'
            value={formData.status}
            label='Status'
            data={statusList}
            onChange={(val) => handleChangeForm('status', val)}
            error={validationForm.status.isError ? `${validationForm.status.message}` : ''}
            size='xs'
            allowDeselect={false}
            withAsterisk
          />
        </Box>
        <Box mb='sm'>
          <Textarea
            name='note'
            label="Note"
            placeholder="Input note"
            value={formData.note}
            onChange={(val) => handleChangeForm('note', val.target.value)}
            size='xs'
            autosize
            minRows={4}
            maxRows={4}
          />
        </Box>
        <Box mt={20}>
          <Flex justify='flex-end'>
            <Group>
              <Button size='xs' variant="outline" onClick={onCloseForm}>Close</Button>
              <Button size='xs' variant="filled" loading={loadingForm} onClick={() => submitOrderQc(formData)}>Add</Button>
            </Group>
          </Flex>
        </Box>
      </Box>
    )
  }


  return (
    <Box>
      {loadingStatus ? <LoadingData /> : loadForm()}
    </Box>
  )
}

export default FormAddOrderQc